import React, { useState, useEffect } from "react";
import { Button, Card, Table, ListGroup, Row, Col, Container } from "react-bootstrap";
import { toast, ToastContainer, } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditPlanForm from "./EditPlanForm";
import IocnCheck from '../../images/icon-check.png';
import AppConstants from "../../AppConstants";

import './styles.scss';

interface PricingTier {
  users: number;
  amount: number;
  stripePriceId?: string;
}

interface Plan {
  id: number;
  name: string;
  billing: string;
  features: string[];
  pricingTiers: PricingTier[];
}

interface ApiTier {
  licenses: number;
  unitAmount: number;
  stripePriceId: string;
  stripeProductId: string;
  _id: string;
  recurringInterval: string;
  currency: string;
  name: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  pricingConfigs: Array<{
    _id: string;
    tiers: ApiTier[];
    currency: string;
    createdBy: string;
    updatedAt: string;
    __v: number;
  }>;
}

// API response interface for plan details
interface PlanDetailsResponse {
  success: boolean;
  message: string;
  data: {
    _id: string;
    details: string[];
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

const initialPlans: Plan[] = [
  {
    id: 1,
    name: "Standard Plan",
    billing: "MONTHLY",
    features: [], // Start with empty features, will be populated from API
    pricingTiers: []
  }
];

const SubscriptionPlanManagement: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>(initialPlans);
  const [showEditCanvas, setShowEditCanvas] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [planDetailsLoading, setPlanDetailsLoading] = useState(true);

  useEffect(() => {
    // Fetch both pricing data and plan details
    Promise.all([fetchPricingData(), fetchPlanDetails()]);
  }, []);

  // Get authentication token
  const getAuthToken = (): string | null => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error("Authentication token not found. Please login again.");
      return null;
    }
    return token;
  };

  const fetchPlanDetails = async () => {
    try {
      setPlanDetailsLoading(true);
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(AppConstants.ADMIN_GET_PLAN_DETAILS_API, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // console.log('Plan Details Response status:', response.status);

      const data: PlanDetailsResponse = await response.json();
      // console.log('Plan Details API Response:', data);

      if (data.success && data.data && data.data.details) {
        // Update plans with fetched plan details
        setPlans(prevPlans =>
          prevPlans.map(plan => ({
            ...plan,
            features: data.data.details
          }))
        );
        // console.log('Plan details loaded:', data.data.details);
      } else {
        // console.log('No plan details found or invalid response structure');
        // Keep default empty features if no plan details found
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch plan details";
      toast.error(`Error loading plan details: ${errorMessage}`);
    } finally {
      setPlanDetailsLoading(false);
    }
  };

  const fetchPricingData = async () => {
    try {
      setLoading(true);

      // Get token from localStorage or wherever you store it
      const token = localStorage.getItem('authToken');

      // console.log("tokennnnn", token)

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(AppConstants.ADMIN_GET_PRICES_API, {
        method: 'GET',
        headers,
      });

      // console.log('Response status:', response.status);
      // console.log('Response:', response);

      const data: ApiResponse = await response.json();
      // console.log('API Response data:', data);

      if (data.success && data.pricingConfigs && data.pricingConfigs.length > 0) {
        // Transform API data to match the component's structure
        const apiTiers = data.pricingConfigs[0].tiers;
        // console.log('API Tiers:', apiTiers);

        // Sort by licenses (users) in ascending order
        const sortedTiers = apiTiers.sort((a, b) => a.licenses - b.licenses);

        // Map API data to component format - IMPORTANT: Include stripePriceId
        const transformedTiers: PricingTier[] = sortedTiers.map(tier => ({
          users: tier.licenses,
          amount: tier.unitAmount,
          stripePriceId: tier.stripePriceId // This is crucial for the update API
        }));

        // console.log('Transformed tiers:', transformedTiers);

        // Update the plans with fetched pricing data
        setPlans(prevPlans =>
          prevPlans.map(plan => ({
            ...plan,
            pricingTiers: transformedTiers
          }))
        );
        // toast.success("Pricing data loaded successfully!");
      } else {
        // console.log('Invalid data structure or no pricing configs found');
        // toast.error("Failed to load pricing data - Invalid response format");
      }
    } catch (error) {
      console.error('Error fetching pricing data:', error);
      if (error instanceof Error) {
        toast.error(`Error loading pricing data: ${error.message}`);
      } else {
        toast.error("Error loading pricing data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = (plan: Plan) => {
    setCurrentPlan(plan);
    setShowEditCanvas(true);
  };

  const handleCloseCanvas = () => {
    setShowEditCanvas(false);
    setCurrentPlan(null);
  };

  const handleUpdatePlan = (values: Plan) => {
    const updatedPlans = plans.map(plan =>
      plan.id === values.id ? values : plan
    );
    setPlans(updatedPlans);
    // toast.success("Plan updated successfully!");
    handleCloseCanvas();

    // Refresh plan details to ensure consistency
    fetchPlanDetails();
  };

  // NEW: Handle plan details updates from EditPlanForm
  const handlePlanDetailsUpdate = (updatedDetails: string[]) => {
    // console.log('Updating plan details in parent:', updatedDetails);

    // Update all plans with the new details immediately
    setPlans(prevPlans =>
      prevPlans.map(plan => ({
        ...plan,
        features: updatedDetails
      }))
    );

    // Also update the currentPlan if it exists
    if (currentPlan) {
      setCurrentPlan(prevPlan =>
        prevPlan ? {
          ...prevPlan,
          features: updatedDetails
        } : null
      );
    }
  };

  // if (loading || planDetailsLoading) {
  if (loading) {
    return (
      <Container fluid className="bg-primary p-4 mb-5 position-relative" style={{ borderRadius: '16px' }}>
        <div className="text-center text-white">
          <h3>Loading {loading ? 'pricing data' : 'plan details'}...</h3>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className=" bg-primary p-4 mb-5 position-relative" style={{ borderRadius: '16px' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <div
        className="position-absolute subscription-block w-25"
        style={{
          left: '50%',
          transform: 'translateX(-50%)',
          top: '-20px'
        }}
      >
        <Button variant="white" className="w-100 bg-white" style={{ border: '5px solid #08A45D' }}>
          MONTHLY
        </Button>
      </div>

      {plans.map(plan => <h1 key={`title-${plan.id}`} className="text-center text-white my-4" style={{ fontSize: '24px' }}>{plan.name}</h1>)}

      {plans.map(plan => (
        <Card key={plan.id} className="plan-card mb-4 p-0 p-md-4 position-relative" style={{ backgroundColor: '#C4FFE4', borderRadius: '16px' }} >
          <Card.Body>
            <Row>
              <Col lg={5} md={12}>
                <Card className="h-100 p-4" style={{ borderRadius: '16px' }}>
                  <div className="features-section">
                    <h4 className="text-light mb-4">What's Included</h4>
                    {plan.features.length > 0 ? (
                      <ul className="feature-list text-secondary">
                        {plan.features.map((feature, index) => (
                          <li key={index} style={{ fontSize: '18px' }}>
                            <img src={IocnCheck} alt='icon' className="me-3" /> {feature}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-secondary" style={{ fontSize: '16px' }}>
                        <p>No plan details available.</p>
                        <p>Click "Edit Plan" to add plan features.</p>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
              <Col lg={7} md={12}>
                <div className="pricing-section">
                  <ListGroup>
                    <Row>
                      <Col md={6}>
                        <ListGroup.Item
                          className="d-flex justify-content-between align-items-start py-3 px-0 border-0 bg-transparent"
                        >
                          <div className="ms-2 me-auto">
                            <p className="text-dark mb-0 gilroy-semibold" style={{ fontSize: '20px' }}>Users</p>
                          </div>
                          <p className="text-dark mb-0 gilroy-semibold" style={{ fontSize: '20px' }}>Amount</p>
                        </ListGroup.Item>
                      </Col>
                      <Col md={6}>
                        <ListGroup.Item
                          className="d-flex justify-content-between align-items-start py-3 px-0 border-0 bg-transparent"
                        >
                          <div className="ms-2 me-auto">
                            <p className="text-dark mb-0 gilroy-semibold" style={{ fontSize: '20px' }}>Users</p>
                          </div>
                          <p className="text-dark mb-0 gilroy-semibold" style={{ fontSize: '20px' }}>Amount</p>
                        </ListGroup.Item>
                      </Col>
                    </Row>

                    {plan.pricingTiers.length > 0 && Array.from({ length: Math.ceil(plan.pricingTiers.length / 2) }).map((_, rowIndex) => (
                      <Row key={rowIndex}>
                        <Col md={6}>
                          {plan.pricingTiers[rowIndex * 2] && (
                            <ListGroup.Item
                              className="d-flex justify-content-between align-items-start py-3 bg-transparent"
                            >
                              <div className="ms-2 me-auto">
                                <p className="text-secondary mb-0" style={{ fontSize: '18px' }}>
                                  {plan.pricingTiers[rowIndex * 2].users}
                                </p>
                              </div>
                              <p className="text-secondary mb-0" style={{ fontSize: '18px' }}>
                                ${plan.pricingTiers[rowIndex * 2].amount}
                              </p>
                            </ListGroup.Item>
                          )}
                        </Col>
                        <Col md={6}>
                          {plan.pricingTiers[rowIndex * 2 + 1] && (
                            <ListGroup.Item
                              className="d-flex justify-content-between align-items-start py-3 bg-transparent"
                            >
                              <div className="ms-2 me-auto">
                                <p className="text-secondary mb-0" style={{ fontSize: '18px' }}>
                                  {plan.pricingTiers[rowIndex * 2 + 1].users}
                                </p>
                              </div>
                              <p className="text-secondary mb-0" style={{ fontSize: '18px' }}>
                                ${plan.pricingTiers[rowIndex * 2 + 1].amount}
                              </p>
                            </ListGroup.Item>
                          )}
                        </Col>
                      </Row>
                    ))}
                  </ListGroup>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}

      {plans.map(plan => (
        <div
          key={`edit-btn-${plan.id}`}
          className="position-absolute subscription-block w-25"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: '30px'
          }}
        >
          <Button
            variant="secondary"
            className="w-100"
            onClick={() => handleEditPlan(plan)}
          >
            Edit Plan
          </Button>
        </div>
      ))}

      {currentPlan && (
        <EditPlanForm
          show={showEditCanvas}
          onHide={handleCloseCanvas}
          plan={currentPlan}
          onUpdate={handleUpdatePlan}
          onPlanDetailsUpdate={handlePlanDetailsUpdate} // NEW: Pass the handler
        />
      )}
    </Container>
  );
};

export default SubscriptionPlanManagement;